// Family relationship options (with maternal/paternal split for extended family)
export type FamilyRelationship =
  | 'mother' | 'father'
  | 'brother' | 'sister'
  | 'maternal-grandmother' | 'maternal-grandfather'
  | 'paternal-grandmother' | 'paternal-grandfather'
  | 'maternal-aunt' | 'maternal-uncle'
  | 'paternal-aunt' | 'paternal-uncle'
  | 'cousin' | 'son' | 'daughter';

// Condition within a family member
export interface FamilyConditionLocal {
  id: string; // UUID for React key
  conditionName: string;
  ageAtOnset?: string; // "40s", "childhood", "unknown", or specific age
}

// Family member
export interface FamilyMemberLocal {
  localId: string; // UUID for React key
  fhirId?: string; // FamilyMemberHistory/{id} if exists in backend
  relationship: FamilyRelationship;
  name?: string; // Optional, for user's reference
  isDeceased: boolean;
  ageOrAgeAtDeath?: string; // Current age if living, age at death if deceased
  causeOfDeath?: string; // Only if deceased
  conditions: FamilyConditionLocal[];
  notes?: string;
  isDeleted?: boolean;
}

export interface FamilyHistoryData {
  familyMembers: FamilyMemberLocal[];
}

// ===== FHIR CONSTANTS =====

// Relationship codes mapping to FHIR v3-RoleCode
export const RELATIONSHIP_CODES: Record<FamilyRelationship, { code: string; display: string }> = {
  'mother': { code: 'MTH', display: 'Mother' },
  'father': { code: 'FTH', display: 'Father' },
  'brother': { code: 'BRO', display: 'Brother' },
  'sister': { code: 'SIS', display: 'Sister' },
  'maternal-grandmother': { code: 'MGRMTH', display: 'Maternal Grandmother' },
  'maternal-grandfather': { code: 'MGRFTH', display: 'Maternal Grandfather' },
  'paternal-grandmother': { code: 'PGRMTH', display: 'Paternal Grandmother' },
  'paternal-grandfather': { code: 'PGRFTH', display: 'Paternal Grandfather' },
  'maternal-aunt': { code: 'MAUNT', display: 'Maternal Aunt' },
  'maternal-uncle': { code: 'MUNCLE', display: 'Maternal Uncle' },
  'paternal-aunt': { code: 'PAUNT', display: 'Paternal Aunt' },
  'paternal-uncle': { code: 'PUNCLE', display: 'Paternal Uncle' },
  'cousin': { code: 'COUSN', display: 'Cousin' },
  'son': { code: 'SON', display: 'Son' },
  'daughter': { code: 'DAU', display: 'Daughter' },
};

// Reverse lookup: FHIR code to our relationship type
export const RELATIONSHIP_CODE_TO_TYPE: Record<string, FamilyRelationship> = {
  'MTH': 'mother',
  'FTH': 'father',
  'BRO': 'brother',
  'SIS': 'sister',
  'MGRMTH': 'maternal-grandmother',
  'MGRFTH': 'maternal-grandfather',
  'PGRMTH': 'paternal-grandmother',
  'PGRFTH': 'paternal-grandfather',
  'MAUNT': 'maternal-aunt',
  'MUNCLE': 'maternal-uncle',
  'PAUNT': 'paternal-aunt',
  'PUNCLE': 'paternal-uncle',
  'COUSN': 'cousin',
  'SON': 'son',
  'DAU': 'daughter',
  // Fallbacks for generic grandparent codes (map to maternal by default)
  'GRMTH': 'maternal-grandmother',
  'GRFTH': 'maternal-grandfather',
  'AUNT': 'maternal-aunt',
  'UNCLE': 'maternal-uncle',
};

// Grouped options for Select dropdown
export const RELATIONSHIP_OPTIONS = [
  { group: 'Parents', items: [
    { value: 'mother', label: 'Mother' },
    { value: 'father', label: 'Father' },
  ]},
  { group: 'Siblings', items: [
    { value: 'brother', label: 'Brother' },
    { value: 'sister', label: 'Sister' },
  ]},
  { group: 'Grandparents', items: [
    { value: 'maternal-grandmother', label: 'Maternal Grandmother' },
    { value: 'maternal-grandfather', label: 'Maternal Grandfather' },
    { value: 'paternal-grandmother', label: 'Paternal Grandmother' },
    { value: 'paternal-grandfather', label: 'Paternal Grandfather' },
  ]},
  { group: 'Aunts & Uncles', items: [
    { value: 'maternal-aunt', label: 'Maternal Aunt' },
    { value: 'maternal-uncle', label: 'Maternal Uncle' },
    { value: 'paternal-aunt', label: 'Paternal Aunt' },
    { value: 'paternal-uncle', label: 'Paternal Uncle' },
  ]},
  { group: 'Other', items: [
    { value: 'cousin', label: 'Cousin' },
    { value: 'son', label: 'Son' },
    { value: 'daughter', label: 'Daughter' },
  ]},
];

// Flat options for simpler use
export const RELATIONSHIP_OPTIONS_FLAT = [
  { value: 'mother', label: 'Mother' },
  { value: 'father', label: 'Father' },
  { value: 'brother', label: 'Brother' },
  { value: 'sister', label: 'Sister' },
  { value: 'maternal-grandmother', label: 'Maternal Grandmother' },
  { value: 'maternal-grandfather', label: 'Maternal Grandfather' },
  { value: 'paternal-grandmother', label: 'Paternal Grandmother' },
  { value: 'paternal-grandfather', label: 'Paternal Grandfather' },
  { value: 'maternal-aunt', label: 'Maternal Aunt' },
  { value: 'maternal-uncle', label: 'Maternal Uncle' },
  { value: 'paternal-aunt', label: 'Paternal Aunt' },
  { value: 'paternal-uncle', label: 'Paternal Uncle' },
  { value: 'cousin', label: 'Cousin' },
  { value: 'son', label: 'Son' },
  { value: 'daughter', label: 'Daughter' },
];

// FamilyMemberHistory status
export const FAMILY_HISTORY_STATUS = {
  completed: 'completed',
  partial: 'partial',
  healthUnknown: 'health-unknown',
} as const;

// System URL for relationship codes
export const RELATIONSHIP_SYSTEM = 'http://terminology.hl7.org/CodeSystem/v3-RoleCode';
